import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import MedicalRecordsSystem, { EnhancedMedicalRecord, MEDICAL_RECORD_CATEGORIES } from '../lib/medicalRecordsSystem'
import {
  Calendar,
  Clock,
  User,
  FileText,
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Filter,
  Search,
  Download,
  Share2,
  Eye,
  ChevronDown,
  ChevronRight,
  Stethoscope,
  Brain,
  Pill,
  TestTube,
  Microscope,
  Shield,
  Tag,
  BarChart3,
  PieChart,
  Clock as Timeline
} from 'lucide-react'
import { toast } from 'sonner'

interface MedicalHistoryViewerProps {
  patientId: string
  mode: 'patient' | 'doctor'
  showTimeline?: boolean
  showStatistics?: boolean
  showFilters?: boolean
}

interface TimelineEvent {
  id: string
  date: string
  type: string
  title: string
  description: string
  category: string
  priority: string
  doctor: string
  icon: React.ReactNode
  color: string
}

interface HistoryStatistics {
  totalRecords: number
  recordsByType: Record<string, number>
  recordsByPriority: Record<string, number>
  recordsByMonth: Record<string, number>
  averageRecordsPerMonth: number
  lastRecordDate: string
  mostCommonType: string
  criticalRecords: number
}

const CATEGORY_ICONS = {
  consultation: Stethoscope,
  diagnosis: Brain,
  prescription: Pill,
  lab_result: TestTube,
  imaging: Microscope,
  surgery: Activity,
  vaccination: Shield
}

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800 border-gray-200',
  normal: 'bg-blue-100 text-blue-800 border-blue-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  critical: 'bg-red-100 text-red-800 border-red-200'
}

const TYPE_COLORS = {
  consultation: 'bg-blue-500',
  diagnosis: 'bg-red-500',
  prescription: 'bg-green-500',
  lab_result: 'bg-purple-500',
  imaging: 'bg-indigo-500',
  surgery: 'bg-orange-500',
  vaccination: 'bg-teal-500'
}

export default function MedicalHistoryViewer({
  patientId,
  mode,
  showTimeline = true,
  showStatistics = true,
  showFilters = true
}: MedicalHistoryViewerProps) {
  const { user } = useAuth()
  const [records, setRecords] = useState<EnhancedMedicalRecord[]>([])
  const [filteredRecords, setFilteredRecords] = useState<EnhancedMedicalRecord[]>([])
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([])
  const [statistics, setStatistics] = useState<HistoryStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [selectedPriority, setSelectedPriority] = useState('')
  const [dateRange, setDateRange] = useState({ from: '', to: '' })
  const [viewMode, setViewMode] = useState<'timeline' | 'list' | 'statistics'>('timeline')
  const [expandedRecords, setExpandedRecords] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadMedicalHistory()
  }, [patientId])

  useEffect(() => {
    applyFilters()
  }, [records, searchTerm, selectedType, selectedPriority, dateRange])

  const loadMedicalHistory = async () => {
    if (!patientId) return

    try {
      setLoading(true)
      
      const { data, error } = await MedicalRecordsSystem.searchRecords({
        patient_id: patientId,
        status: 'active'
      }, user?.id)

      if (error) {
        toast.error('Erro ao carregar histórico médico')
        console.error(error)
        return
      }

      const sortedRecords = (data || []).sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )

      setRecords(sortedRecords)
      generateTimelineEvents(sortedRecords)
      calculateStatistics(sortedRecords)
    } catch (error) {
      toast.error('Erro ao carregar histórico médico')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...records]

    // Filtro por termo de busca
    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    // Filtro por tipo
    if (selectedType) {
      filtered = filtered.filter(record => record.record_type === selectedType)
    }

    // Filtro por prioridade
    if (selectedPriority) {
      filtered = filtered.filter(record => record.priority === selectedPriority)
    }

    // Filtro por data
    if (dateRange.from) {
      filtered = filtered.filter(record => 
        new Date(record.created_at) >= new Date(dateRange.from)
      )
    }
    if (dateRange.to) {
      filtered = filtered.filter(record => 
        new Date(record.created_at) <= new Date(dateRange.to)
      )
    }

    setFilteredRecords(filtered)
    generateTimelineEvents(filtered)
  }

  const generateTimelineEvents = (records: EnhancedMedicalRecord[]) => {
    const events: TimelineEvent[] = records.map(record => {
      const category = MEDICAL_RECORD_CATEGORIES.find(c => c.id === record.record_type)
      const IconComponent = CATEGORY_ICONS[record.record_type as keyof typeof CATEGORY_ICONS] || FileText
      
      return {
        id: record.id,
        date: record.created_at,
        type: record.record_type,
        title: record.title,
        description: record.description || '',
        category: category?.name || record.record_type,
        priority: record.priority,
        doctor: record.doctor?.full_name || 'Médico não identificado',
        icon: <IconComponent className="h-4 w-4" />,
        color: TYPE_COLORS[record.record_type as keyof typeof TYPE_COLORS] || 'bg-gray-500'
      }
    })

    setTimelineEvents(events)
  }

  const calculateStatistics = (records: EnhancedMedicalRecord[]) => {
    // Filtrar apenas versões atuais ativas
    const activeCurrentRecords = records.filter(record => 
      record.status === 'active' && (record.is_current_version === true || record.is_current_version === null)
    )
    
    if (activeCurrentRecords.length === 0) {
      setStatistics(null)
      return
    }

    const recordsByType: Record<string, number> = {}
    const recordsByPriority: Record<string, number> = {}
    const recordsByMonth: Record<string, number> = {}

    activeCurrentRecords.forEach(record => {
      // Por tipo
      recordsByType[record.record_type] = (recordsByType[record.record_type] || 0) + 1
      
      // Por prioridade
      recordsByPriority[record.priority] = (recordsByPriority[record.priority] || 0) + 1
      
      // Por mês
      const monthKey = new Date(record.created_at).toLocaleDateString('pt-BR', { 
        year: 'numeric', 
        month: '2-digit' 
      })
      recordsByMonth[monthKey] = (recordsByMonth[monthKey] || 0) + 1
    })

    const mostCommonType = Object.entries(recordsByType)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || ''
    
    const criticalRecords = recordsByPriority.critical || 0
    const lastRecordDate = records[0]?.created_at || ''
    
    // Calcular média de registros por mês
    const monthsCount = Object.keys(recordsByMonth).length || 1
    const averageRecordsPerMonth = Math.round(activeCurrentRecords.length / monthsCount)

    setStatistics({
      totalRecords: activeCurrentRecords.length,
      recordsByType,
      recordsByPriority,
      recordsByMonth,
      averageRecordsPerMonth,
      lastRecordDate,
      mostCommonType,
      criticalRecords
    })
  }

  const toggleRecordExpansion = (recordId: string) => {
    const newExpanded = new Set(expandedRecords)
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId)
    } else {
      newExpanded.add(recordId)
    }
    setExpandedRecords(newExpanded)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const exportHistory = () => {
    const csvContent = [
      ['Data', 'Tipo', 'Título', 'Descrição', 'Médico', 'Prioridade'],
      ...filteredRecords.map(record => [
        formatDate(record.created_at),
        record.record_type,
        record.title,
        record.description || '',
        record.doctor?.full_name || '',
        record.priority
      ])
    ].map(row => row.join(',')).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `historico-medico-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    window.URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header com estatísticas resumidas */}
      {showStatistics && statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total de Registros</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.totalRecords}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Média/Mês</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.averageRecordsPerMonth}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Críticos</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.criticalRecords}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Último Registro</p>
                <p className="text-sm font-bold text-gray-900">
                  {statistics.lastRecordDate ? formatDate(statistics.lastRecordDate) : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controles de visualização e filtros */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Modos de visualização */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'timeline'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Timeline className="h-4 w-4 mr-1 inline" />
              Timeline
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FileText className="h-4 w-4 mr-1 inline" />
              Lista
            </button>
            <button
              onClick={() => setViewMode('statistics')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'statistics'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BarChart3 className="h-4 w-4 mr-1 inline" />
              Estatísticas
            </button>
          </div>

          {/* Ações */}
          <div className="flex items-center space-x-2">
            <button
              onClick={exportHistory}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Exportar</span>
            </button>
          </div>
        </div>

        {/* Filtros */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="Buscar registros..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todos os tipos</option>
                  {MEDICAL_RECORD_CATEGORIES.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todas as prioridades</option>
                  <option value="low">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="high">Alta</option>
                  <option value="critical">Crítica</option>
                </select>
              </div>
              <div>
                <input
                  type="date"
                  placeholder="Data inicial"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <input
                  type="date"
                  placeholder="Data final"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Conteúdo principal */}
      {viewMode === 'timeline' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Timeline do Histórico Médico</h3>
          {timelineEvents.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum registro encontrado</p>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              <div className="space-y-6">
                {timelineEvents.map((event, index) => (
                  <div key={event.id} className="relative flex items-start space-x-4">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full ${event.color} flex items-center justify-center text-white z-10`}>
                      {event.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-900">{event.title}</h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[event.priority as keyof typeof PRIORITY_COLORS]}`}>
                            {event.priority === 'low' ? 'Baixa' :
                             event.priority === 'normal' ? 'Normal' :
                             event.priority === 'high' ? 'Alta' : 'Crítica'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{event.category} • Dr. {event.doctor}</span>
                          <span>{formatDate(event.date)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {viewMode === 'list' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Registros Médicos ({filteredRecords.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {filteredRecords.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum registro encontrado</p>
              </div>
            ) : (
              filteredRecords.map((record) => {
                const isExpanded = expandedRecords.has(record.id)
                const category = MEDICAL_RECORD_CATEGORIES.find(c => c.id === record.record_type)
                const IconComponent = CATEGORY_ICONS[record.record_type as keyof typeof CATEGORY_ICONS] || FileText
                
                return (
                  <div key={record.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={`p-2 rounded-lg ${TYPE_COLORS[record.record_type as keyof typeof TYPE_COLORS]} text-white`}>
                          <IconComponent className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-medium text-gray-900">{record.title}</h4>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[record.priority as keyof typeof PRIORITY_COLORS]}`}>
                              {record.priority === 'low' ? 'Baixa' :
                               record.priority === 'normal' ? 'Normal' :
                               record.priority === 'high' ? 'Alta' : 'Crítica'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{record.description}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                            <span>{category?.name}</span>
                            <span>Dr. {record.doctor?.full_name}</span>
                            <span>{formatDate(record.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleRecordExpansion(record.id)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </button>
                    </div>
                    
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                        {record.diagnosis && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Diagnóstico</label>
                            <p className="text-sm text-gray-900">{record.diagnosis}</p>
                          </div>
                        )}
                        {record.treatment && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tratamento</label>
                            <p className="text-sm text-gray-900">{record.treatment}</p>
                          </div>
                        )}
                        {record.tags && record.tags.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                            <div className="flex flex-wrap gap-1">
                              {record.tags.map((tag, index) => (
                                <span key={index} className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}

      {viewMode === 'statistics' && statistics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico por tipo */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Registros por Tipo</h3>
            <div className="space-y-3">
              {Object.entries(statistics.recordsByType).map(([type, count]) => {
                const category = MEDICAL_RECORD_CATEGORIES.find(c => c.id === type)
                const percentage = Math.round((count / statistics.totalRecords) * 100)
                return (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${TYPE_COLORS[type as keyof typeof TYPE_COLORS]}`}></div>
                      <span className="text-sm text-gray-700">{category?.name || type}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${TYPE_COLORS[type as keyof typeof TYPE_COLORS]}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8">{count}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Gráfico por prioridade */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Registros por Prioridade</h3>
            <div className="space-y-3">
              {Object.entries(statistics.recordsByPriority).map(([priority, count]) => {
                const percentage = Math.round((count / statistics.totalRecords) * 100)
                const priorityColors = {
                  low: 'bg-gray-500',
                  normal: 'bg-blue-500',
                  high: 'bg-orange-500',
                  critical: 'bg-red-500'
                }
                const priorityLabels = {
                  low: 'Baixa',
                  normal: 'Normal',
                  high: 'Alta',
                  critical: 'Crítica'
                }
                return (
                  <div key={priority} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${priorityColors[priority as keyof typeof priorityColors]}`}></div>
                      <span className="text-sm text-gray-700">{priorityLabels[priority as keyof typeof priorityLabels]}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${priorityColors[priority as keyof typeof priorityColors]}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8">{count}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Registros por mês */}
          <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Registros por Mês</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(statistics.recordsByMonth).map(([month, count]) => (
                <div key={month} className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{count}</div>
                  <div className="text-sm text-gray-600">{month}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}